import {
  INTERCOM_TRIGGER,
  type IntercomTrigger,
} from '@sparklayer-io/spark-dash-core/constants/intercom';
import { t } from 'i18next';
import type { ReactNode } from 'react';
import { useUser } from '../context/page-context';
import { IntercomLink } from './intercom-link';

/**
 * Which parts of a setting row are limited to SparkLayer staff. Undefined means everyone.
 * - `read-write`: hidden from merchants, badged for staff. Any row, handled by SettingRow.
 * - `write`: merchants see it read-only with a route to support. Toggle rows only.
 */
export type StaffAccess = 'read-write' | 'write';

/** Locks a `write` row for merchants and explains why, with a link to support. */
export function useStaffWriteLock({
  staff,
  contactTrigger = INTERCOM_TRIGGER.contact,
}: {
  staff?: StaffAccess;
  /** Intercom trigger for the support link. */
  contactTrigger?: IntercomTrigger;
}): { locked: boolean; helpText?: ReactNode } {
  const { isSparkStaff } = useUser();

  if (staff !== 'write' || isSparkStaff) {
    return { locked: false };
  }

  return {
    locked: true,
    helpText: (
      <>
        {t('components:staff_setting.help')}{' '}
        <IntercomLink trigger={contactTrigger} size="sm">
          {t('components:staff_setting.contact')}
        </IntercomLink>
      </>
    ),
  };
}
